import store from '@/store';

export class ChatService {
  constructor() {
    this.chats = []; 
    this.activeChatId = null; // Apenas em memória
    this.conversationId = null;
    this.useAPI = true; // Ativa uso de API por padrão
  }

  // Sincroniza lista de chats com a API
  async syncChatsFromAPI(filters = {}) {
    try {
      // Constrói os parâmetros da query
      const params = new URLSearchParams();

      if (filters.limit) params.append('limit', filters.limit);
      if (filters.offset) params.append('offset', filters.offset);
      if (filters.search) params.append('search', filters.search);

      const queryString = params.toString();
      const apiPath = queryString
        ? `/api/ai-chat/conversations?${queryString}`
        : '/api/ai-chat/conversations';

      const [response, playbookNames] = await Promise.all([
        store.dispatch('api/apiGet', { apiRelativePath: apiPath }),
        this._getPlaybookNames()
      ]);

      const conversations = response.data;

      const mappedChats = conversations.map(conv => {
        let rawMessages = conv.messages || [];
        
        // A API adiciona uma hashtag pro contador (ex: 'novo roteiro 2303 #8'), então validamos o nome original
        const isPlaybookChat = playbookNames.some(name => 
          conv.title && (conv.title === name || conv.title.startsWith(`${name} #`))
        );

        // Quando for playbook, ignora messages[0] (instrução do roteiro) para que mostre só a resposta
        if (isPlaybookChat && rawMessages.length > 0 && rawMessages[0].role === 'user') {
          rawMessages = rawMessages.slice(1);
        }

        return {
          id: conv.id,
          title: conv.title,
          conversationId: conv.id,
          user_id: conv.user_id,
          agent_type: conv.agent_type,
          status: conv.status,
          metadata: conv.metadata,
          createdAt: new Date(conv.created_at),
          updatedAt: new Date(conv.updated_at),
          messages: rawMessages.map(msg => ({
            id: msg.id,
            sender: msg.role === 'user' ? 'user' : 'ai',
            text: msg.content,
            timestamp: msg.timestamp,
            metadata: msg.metadata,
            agent_id: msg.metadata?.agent_type || null,
            role: msg.role,
            content: msg.content,
            conversation_id: msg.conversation_id,
            created_at: msg.created_at,
            updated_at: msg.updated_at
          }))
        };
      });

      // Se for uma busca, retorna os resultados
      if (filters.search) {
        return mappedChats;
      }

      // Se não for busca, atualiza this.chats
      this.chats = mappedChats;

      // Ordena os chats por updatedAt (mais recente primeiro)
      this.sortChatsByUpdatedAt();

      return this.chats;
    } catch (error) {
      throw error;
    }
  }

  // Carrega chats da API
  async loadChatsFromAPI() {
    return this.syncChatsFromAPI();
  }

  // Obtém chat ativo
  getActiveChatId() {
    return this.activeChatId;
  }

  // Define chat ativo
  setActiveChatId(chatId) {
    this.activeChatId = chatId;
  }

  // Atualiza o título do chat
  async renameChat(chatId, newTitle) {
    try {

      const response = await store.dispatch('api/apiUpdate', {
        crudData: {
          path: `/api/ai-chat/conversations/${chatId}`,
          saveData: { title: newTitle }
        }
      });

      // Atualiza localmente após sucesso na API
      const chat = this.chats.find(c => c.id === chatId);
      if (chat) {
        chat.title = response.data.title;
        chat.updatedAt = new Date(response.data.updated_at);
        return true;
      }
      return false;
    } catch (error) {
      console.error('Erro ao renomear chat:', error);
      throw error;
    }
  }

  // Cria novo chat via API
  async createNewChat(title = 'Novo Chat') {
    try {
      // Chamada à API para criar conversa
      const response = await store.dispatch('api/apiAdd', {
        path: '/api/ai-chat/conversations',
        saveData: { title: title }
      });

      const newChat = {
        id: response.data.id,
        title: response.data.title,
        conversationId: response.data.id,
        user_id: response.data.user_id,
        agent_type: response.data.agent_type,
        status: response.data.status,
        metadata: response.data.metadata,
        createdAt: new Date(response.data.created_at),
        updatedAt: new Date(response.data.updated_at),
        messages: []
      };

      this.chats.unshift(newChat);
      this.setActiveChatId(newChat.id);

      return newChat;
    } catch (error) {
      throw error;
    }
  }

  // Adiciona mensagem ao chat
  addMessageToChat(chatId, message) {
    const chat = this.chats.find(c => c.id === chatId);
    if (chat) {
      const newMessage = {
        id: message.id || `msg_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`,
        sender: message.sender,
        text: message.text || null,
        timestamp: new Date(),
        agent_id: message.agent_id || null
      };
      chat.messages.push(newMessage);
      chat.updatedAt = new Date();

      // Reordena os chats para mover o chat atualizado para o topo
      this.sortChatsByUpdatedAt();

      return newMessage;
    }
    return null;
  }

  // Ordena os chats por updatedAt (mais recente primeiro)
  sortChatsByUpdatedAt() {
    this.chats.sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  }

  async generateResponse(userMessage) {
    const activeChat = this.getActiveChat();
    const conversationId = activeChat?.conversationId || null;

    const payload = {
      message: userMessage
    };

    if (conversationId) {
      payload.conversation_id = conversationId;
    }

    const response = await store.dispatch('api/apiAdd', {
      path: '/api/ai-chat/chat',
      saveData: payload
    });

    if (activeChat) {
      activeChat.conversationId = response.data.conversation_id;
    }

    return {
      sender: 'ai',
      text: response.data.message.content,
      id: response.data.message.id || null,
      agent_id: response.data.message.metadata?.agent_type || response.data.metadata?.agent_type || null
    };
  }

  // Remove chat (via API se useAPI=true)
  async deleteChat(chatId) {
    try {
      if (this.useAPI) {
        // Deleta via API
        await store.dispatch('api/apiDelete', {
          crudData: { path: `/api/ai-chat/conversations/${chatId}` }
        });
      }
    } catch (error) {
      throw error;
    }

    // Remove do estado local
    this.chats = this.chats.filter(chat => chat.id !== chatId);

    // Se o chat deletado era o ativo, limpa o chat ativo
    if (this.activeChatId === chatId) {
      this.clearActiveChat();
    }
  }

  // Limpa chat ativo
  clearActiveChat() {
    this.activeChatId = null;
  }

  getAllChats() {
    return this.chats;
  }

  getActiveChat() {
    const activeChat = this.chats.find(chat => chat.id === this.activeChatId) || null;
    return activeChat;
  }

  getChatById(chatId) {
    const chat = this.chats.find(chat => chat.id === chatId) || null;
    return chat;
  }

  // Carrega detalhes de um chat específico da API
  async getChatFromAPI(chatId, fromPlaybook = false) {
    try {
      if (this.useAPI) {
        const [response, playbookNames] = await Promise.all([
          store.dispatch('api/apiGet', { apiRelativePath: `/api/ai-chat/conversations/${chatId}` }),
          this._getPlaybookNames()
        ]);

        const localChat = this.chats.find(c => c.id === chatId);

        let rawMessages = response.data.messages || [];
        
        // A API adiciona uma hashtag pro contador (ex: 'novo roteiro 2303 #8'), então validamos o nome original
        const isPlaybookChat = playbookNames.some(name => 
          response.data.title && (response.data.title === name || response.data.title.startsWith(`${name} #`))
        );

        // Quando vem de playbook (explícito ou heurística), ignora messages[0] (instrução do roteiro) e exibe só a resposta
        if (fromPlaybook || (isPlaybookChat && rawMessages.length > 0 && rawMessages[0].role === 'user')) {
          rawMessages = rawMessages.slice(1);
        }

        if (localChat) {
          localChat.messages = rawMessages.map(msg => ({
            id: msg.id,
            sender: msg.role === 'user' ? 'user' : 'ai',
            text: msg.content,
            timestamp: msg.timestamp,
            metadata: msg.metadata,
            agent_id: msg.metadata?.agent_type || null,
            role: msg.role,
            content: msg.content,
            conversation_id: msg.conversation_id,
            created_at: msg.created_at,
            updated_at: msg.updated_at
          }));
          localChat.title = response.data.title;
          localChat.user_id = response.data.user_id;
          localChat.agent_type = response.data.agent_type;
          localChat.status = response.data.status;
          localChat.metadata = response.data.metadata;
          localChat.updatedAt = new Date(response.data.updated_at);
        }

        return {
          id: response.data.id,
          title: response.data.title,
          conversationId: response.data.id,
          user_id: response.data.user_id,
          agent_type: response.data.agent_type,
          status: response.data.status,
          metadata: response.data.metadata,
          createdAt: new Date(response.data.created_at),
          updatedAt: new Date(response.data.updated_at),
          messages: rawMessages
        };
      }
    } catch (error) {
      throw error;
    }

    return this.getChatById(chatId);
  }

  // Retorna mensagem de boas-vindas 
  async getGreetingMessage() {
    const response = await store.dispatch('api/apiGet', {
      apiRelativePath: '/api/ai-chat/greeting'
    });
    return response.data.message;
  }

  // Retorna resultados de busca de chats 
  async searchChatsFromAPI(searchTerm, limit = 20, offset = 0) {
    return this.syncChatsFromAPI({
      search: searchTerm,
      limit,
      offset
    });
  }

  // Registra feedback (positivo/negativo) em uma mensagem do assistente
  async postMessageFeedback(messageId, rating) {
    // rating: "up" | "down"
    const response = await store.dispatch('api/apiAdd', {
      path: `/api/ai-chat/messages/${messageId}/feedback`,
      saveData: { rating }
    });
    return response;
  }

  // Helper para obter playbooks e cachear seus nomes (usado para ocultar instruções em playbooks executados)
  async _getPlaybookNames() {
    if (this._playbookNames) return this._playbookNames;
    try {
      const response = await store.dispatch('api/apiGet', {
        apiRelativePath: '/api/ai-chat/playbooks'
      });
      const items = response.data?.items || [];
      this._playbookNames = items.map(p => p.nome);
      return this._playbookNames;
    } catch (e) {
      return [];
    }
  }
}